import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import codeService from "../../services/codeService";
import {getAccountScanPermissions} from "store/slices/scanPermissionsSlice";

export const startRepositoryScan = createAsyncThunk(
    "code-scan/startRepositoryScan",
    async (formData, {rejectWithValue, dispatch, getState}) => {
        try {
            const response = await codeService.startRepositoryScan(formData);

            if (response?.status === 200 || response?.status === 204) {
                const state = getState();
                const bodyRequest = state?.code?.lastRepositoryListBodyRequest;

                dispatch(getAccountScanPermissions());
                dispatch(getScannedRepositories(bodyRequest));
            }

            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    });

export const updateRepository = createAsyncThunk(
    "code-scan/updateRepository",
    async ({id, data}, {rejectWithValue, dispatch, getState}) => {
        try {
            const response = await codeService.updateRepository(id, data);

            if (response?.status === 200 || response?.status === 204) {
                const state = getState();
                const bodyRequest = state?.code?.lastRepositoryListBodyRequest;

                dispatch(getScannedRepositories(bodyRequest));
            }

            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getScannedRepositories = createAsyncThunk(
    "code-scan/getScannedRepositories",
    async (arg, {rejectWithValue, dispatch}) => {
        try {
            const {silent, ...requestData} = arg || {};
            dispatch(setLastRepositoryListBodyRequest(requestData));
            return await codeService.getScannedRepositories(requestData);
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    });

export const startScanWithBranchName = createAsyncThunk(
    "code-scan/startScanWithBranchName",
    async ({id, params}, {rejectWithValue, dispatch, getState}) => {
        try {
            const response = await codeService.startScanWithBranchName(id, params);

            if (response?.status === 200 || response?.status === 204) {
                const state = getState();
                const bodyRequest = state?.code?.lastRepositoryListBodyRequest;

                dispatch(getAccountScanPermissions());
                dispatch(getScannedRepositories(bodyRequest));
            }

            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const exportRepositoriesCsv = createAsyncThunk(
    "code-scan/exportRepositoriesCsv",
    async (data, {rejectWithValue}) => {
        try {
            const response = await codeService.exportRepositoriesCsv(data);

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "repositories.csv");
            document.body.appendChild(link);
            link.click();
            link.remove();

            return true;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getCodeDetailList = createAsyncThunk(
    "code-scan/getCodeDetailList",
    async (data, {rejectWithValue}) => {
        try {
            return await codeService.getCodeDetailList(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    });

export const getCodeScanDetails = createAsyncThunk(
    "code-scan/getCodeScanDetails",
    async ({id, navigate}, {rejectWithValue, dispatch}) => {
        try {
            const response = await codeService.getCodeScanDetails(id);
            return {
                data: response?.data,
                navigate
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    });

const initialState = {
    repositoryList: null,
    lastRepositoryListBodyRequest: null,
    errorMessage: null,
    successMessage: null,
    selectedRepo: null,
    repoModalUpdate: false,
    repoScanStep: 0,
    repoScanFailed: false,
    repoModalLoading: false,
    exportLoading: false,
    repoListLoading: false,
    repoScanDetailList: null,
    repoScanDetailListLoading: false,
    detailsLoading: false,
    repoScanDetails: null,
    repositoryName: null
};

export const codeSlice = createSlice({
    name: 'codeSlice',
    initialState,
    reducers: {
        resetMessages: (state) => {
            state.errorMessage = null;
            state.successMessage = null;
        },
        setModalUpdate: (state, action) => {
            state.repoModalUpdate = action.payload;
        },
        setSelectedRepo: (state, action) => {
            state.selectedRepo = action.payload;
        },
        setLastRepositoryListBodyRequest: (state, action) => {
            state.lastRepositoryListBodyRequest = action.payload;
        },
        setRepoScanStep: (state, action) => {
            state.repoScanStep = action.payload;
        },
        setRepoScanFailed: (state, action) => {
            state.repoScanFailed = action.payload;
        },
        setRepositoryName: (state, action) => {
            state.repositoryName = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(startRepositoryScan.pending, (state, action) => {
            state.modalLoading = true;
            state.repoScanStep = 1;
            state.errorMessage = null;
        });
        builder.addCase(startRepositoryScan.fulfilled, (state, action) => {
            state.modalLoading = false;
            state.repoScanStep = 2;
        });
        builder.addCase(startRepositoryScan.rejected, (state, action) => {
            state.modalLoading = false;
            state.repoScanFailed = true;
            state.repoScanStep = 2;
            state.errorMessage = action?.payload?.error || action?.payload?.response?.data?.error || 'error';
        });
        builder.addCase(updateRepository.pending, (state, action) => {
            state.modalLoading = true;
            state.repoScanStep = 1;
        });
        builder.addCase(updateRepository.fulfilled, (state, action) => {
            state.modalLoading = false;
            state.repoScanStep = 2;
        });
        builder.addCase(updateRepository.rejected, (state, action) => {
            state.modalLoading = false;
            state.repoScanFailed = true;
            state.repoScanStep = 2;
        });
        builder.addCase(getScannedRepositories.fulfilled, (state, action) => {
            state.repositoryList = action.payload.data;
            state.repoListLoading = false;
        });
        builder.addCase(getScannedRepositories.pending, (state, action) => {
            if (!action.meta.arg?.silent) {
                state.repoListLoading = true;
            }
        });
        builder.addCase(getScannedRepositories.rejected, (state) => {
            state.repoListLoading = false;
        });
        builder.addCase(exportRepositoriesCsv.pending, (state) => {
            state.exportLoading = true;
        });
        builder.addCase(exportRepositoriesCsv.fulfilled, (state) => {
            state.exportLoading = false;
        });
        builder.addCase(exportRepositoriesCsv.rejected, (state, action) => {
            state.exportLoading = false;
        });
        builder.addCase(getCodeDetailList.pending, (state) => {
            state.repoScanDetailListLoading = true;
            state.repoScanDetailList = null;
        });
        builder.addCase(getCodeDetailList.fulfilled, (state, action) => {
            state.repoScanDetailListLoading = false;
            state.repoScanDetailList = action.payload?.data;
        });
        builder.addCase(getCodeDetailList.rejected, (state, action) => {
            state.repoScanDetailListLoading = false;
        });
        builder.addCase(getCodeScanDetails.pending, (state) => {
            state.detailsLoading = true;
        });
        builder.addCase(getCodeScanDetails.fulfilled, (state, action) => {
            state.detailsLoading = false;
            state.repoScanDetails = action.payload?.data;
            action.payload?.navigate(`/app/code/code-detail`);
        });
        builder.addCase(getCodeScanDetails.rejected, (state, action) => {
            state.detailsLoading = false;
        });
        builder.addCase(startScanWithBranchName.rejected, (state, action) => {
            state.errorMessage = action?.payload?.error || action?.payload?.response?.data?.error || 'error';
        });
        builder.addCase(startScanWithBranchName.pending, (state) => {
            state.errorMessage = null;
        });
    }
});

export const {
    resetMessages,
    setModalUpdate,
    setSelectedRepo,
    setLastRepositoryListBodyRequest,
    setRepoScanStep,
    setRepoScanFailed,
    setRepositoryName
} = codeSlice.actions;

export default codeSlice.reducer;

